import { useState, useRef, useEffect } from 'react';
import { motion } from 'framer-motion';

const transformations = [
  {
    id: 1,
    title: 'Whitney Kitchen Renovation',
    description:
      'Stunning kitchen transformation with custom cabinetry and modern design elements',
    before:
      'https://res.cloudinary.com/dwoubdppz/image/upload/w_800,q_auto,f_auto/v1755313590/oak-and-stone-remodel/before-after/whitney-kitchen-before.webp',
    after:
      'https://res.cloudinary.com/dwoubdppz/image/upload/w_800,q_auto,f_auto/v1755313591/oak-and-stone-remodel/before-after/whitney-kitchen-after.webp',
    category: 'Whitney Kitchen'
  },
  {
    id: 2,
    title: 'Whitney Kitchen Modern Update',
    description:
      'Contemporary kitchen upgrade with sleek design and premium finishes',
    before:
      'https://res.cloudinary.com/dwoubdppz/image/upload/w_800,q_auto,f_auto/v1755313589/oak-and-stone-remodel/before-after/whitney-kitchen-before-2.webp',
    after:
      'https://res.cloudinary.com/dwoubdppz/image/upload/w_800,q_auto,f_auto/v1755313588/oak-and-stone-remodel/before-after/whitney-kitchen-after-2.webp',
    category: 'Whitney Kitchen 2'
  },
  {
    id: 3,
    title: 'Kerwin Kitchen Transformation',
    description:
      'Complete kitchen remodel with custom cabinets, modern appliances, and beautiful countertops',
    before:
      'https://res.cloudinary.com/dwoubdppz/image/upload/w_800,q_auto,f_auto/v1755313941/oak-and-stone-remodel/before-after/kerwin-kitchen-before.jpg',
    after:
      'https://res.cloudinary.com/dwoubdppz/image/upload/w_800,q_auto,f_auto/v1755313942/oak-and-stone-remodel/before-after/kerwin-kitchen-done.jpg',
    category: 'Kerwin Kitchen'
  }
];

export const BeforeAfterComparison = () => {
  const [currentIndex, setCurrentIndex] = useState(0);
  const [sliderPosition, setSliderPosition] = useState(50);
  const containerRef = useRef(null);

  const handleSliderChange = (e) => {
    setSliderPosition(parseInt(e.target.value));
  };

  const handleMouseMove = (e) => {
    if (containerRef.current) {
      const rect = containerRef.current.getBoundingClientRect();
      const x = e.clientX - rect.left;
      const percentage = (x / rect.width) * 100;
      setSliderPosition(Math.max(0, Math.min(100, percentage)));
    }
  };

  const handleTouchMove = (e) => {
    if (containerRef.current) {
      const rect = containerRef.current.getBoundingClientRect();
      const touch = e.touches[0];
      const x = touch.clientX - rect.left;
      const percentage = (x / rect.width) * 100;
      setSliderPosition(Math.max(0, Math.min(100, percentage)));
    }
  };

  useEffect(() => {
    // Reset slider position when changing transformations
    setSliderPosition(50);
  }, [currentIndex]);

  return (
    <section className="py-16 bg-white">
      <div className="px-4 mx-auto max-w-7xl sm:px-6 lg:px-8">
        {/* Header */}
        <div className="mb-12 text-center">
          <h2 className="mb-4 text-3xl font-bold text-gray-900 md:text-4xl">
            See the Transformation
          </h2>
          <p className="text-xl text-gray-600">
            Drag the slider to see before and after comparisons of our work
          </p>
        </div>

        {/* Transformation Selector */}
        <div className="mb-8">
          <div className="flex flex-wrap gap-4 justify-center">
            {transformations.map((transformation, index) => (
              <button
                key={transformation.id}
                onClick={() => setCurrentIndex(index)}
                className={`px-6 py-3 rounded-full text-sm font-medium transition-all duration-300 ${
                  currentIndex === index
                    ? 'bg-primary text-white shadow-lg'
                    : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                }`}>
                {transformation.category}
              </button>
            ))}
          </div>
        </div>

        {/* Before/After Comparison */}
        <div className="mx-auto max-w-4xl">
          <div className="mb-6 text-center">
            <h3 className="mb-2 text-2xl font-semibold text-gray-900">
              {transformations[currentIndex].title}
            </h3>
            <p className="text-gray-600">
              {transformations[currentIndex].description}
            </p>
          </div>

          {/* Image Comparison Container */}
          <div
            ref={containerRef}
            className="overflow-hidden relative bg-gray-100 rounded-lg shadow-xl cursor-col-resize"
            onMouseMove={handleMouseMove}
            onTouchMove={handleTouchMove}
            style={{ aspectRatio: '16/9' }}>
            {/* After Image */}
            <img
              src={transformations[currentIndex].after}
              alt={`After: ${transformations[currentIndex].title}`}
              className="object-cover absolute inset-0 w-full h-full"
              loading="lazy"
            />

            {/* Before Image (clipped) */}
            <div
              className="overflow-hidden absolute inset-0"
              style={{ clipPath: `inset(0 ${100 - sliderPosition}% 0 0)` }}>
              <img
                src={transformations[currentIndex].before}
                alt={`Before: ${transformations[currentIndex].title}`}
                className="object-cover w-full h-full"
                loading="lazy"
              />
            </div>

            {/* Slider Handle */}
            <div
              className="absolute top-0 bottom-0 w-1 bg-white shadow-lg cursor-col-resize"
              style={{ left: `${sliderPosition}%` }}>
              <div className="flex absolute top-1/2 left-1/2 justify-center items-center w-8 h-8 bg-white rounded-full shadow-lg transform -translate-x-1/2 -translate-y-1/2">
                <svg
                  className="w-4 h-4 text-gray-600"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24">
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M8 9l4-4 4 4m0 6l-4 4-4-4"
                  />
                </svg>
              </div>
            </div>

            {/* Labels */}
            <div className="absolute top-4 left-4 px-3 py-1 text-sm font-medium text-white bg-black bg-opacity-75 rounded">
              Before
            </div>
            <div className="absolute top-4 right-4 px-3 py-1 text-sm font-medium text-white bg-black bg-opacity-75 rounded">
              After
            </div>
          </div>

          {/* Slider Control */}
          <div className="mt-6">
            <input
              type="range"
              min="0"
              max="100"
              value={sliderPosition}
              onChange={handleSliderChange}
              className="w-full h-2 bg-gray-200 rounded-lg appearance-none cursor-pointer slider"
            />
            <div className="flex justify-between mt-2 text-sm text-gray-600">
              <span>Before</span>
              <span>After</span>
            </div>
          </div>
        </div>

        {/* Mobile Instructions */}
        <div className="mt-8 text-center md:hidden">
          <p className="text-sm text-gray-500">
            💡 Tap and drag on the image to see the transformation
          </p>
        </div>

        {/* CTA */}
        <div className="mt-12 text-center">
          <motion.a
            href="/gallery"
            whileHover={{ scale: 1.05 }}
            whileTap={{ scale: 0.95 }}
            className="inline-flex items-center px-8 py-3 text-base font-medium text-white rounded-md border border-transparent transition-colors duration-300 bg-primary hover:bg-primary-hover">
            View More Transformations
            <svg
              className="ml-2 w-4 h-4"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24">
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M14 5l7 7-7 7"
              />
            </svg>
          </motion.a>
        </div>
      </div>

      <style jsx>{`
        .slider::-webkit-slider-thumb {
          appearance: none;
          height: 20px;
          width: 20px;
          border-radius: 50%;
          background: #1e4784;
          cursor: pointer;
          box-shadow: 0 2px 4px rgba(0, 0, 0, 0.2);
        }

        .slider::-moz-range-thumb {
          height: 20px;
          width: 20px;
          border-radius: 50%;
          background: #1e4784;
          cursor: pointer;
          border: none;
          box-shadow: 0 2px 4px rgba(0, 0, 0, 0.2);
        }
      `}</style>
    </section>
  );
};
